import { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import debounce from 'lodash.debounce';

function formattedSuggestion(placePrediction) {
    return {
        mainText:
            (placePrediction && placePrediction.structuredFormat && placePrediction.structuredFormat.mainText) || '',
        secondaryText:
            (placePrediction && placePrediction.structuredFormat && placePrediction.structuredFormat.secondaryText) ||
            ''
    };
}

function compose(...fns) {
    return (...args) => {
        fns.forEach(fn => {
            if (fn) fn(...args);
        });
    };
}

function geocodeByPlaceId(placeId) {
    return new Promise((resolve, reject) => {
        if (!window.google || !window.google.maps) {
            reject(new Error('Google Maps JavaScript API library must be loaded'));
            return;
        }

        const geocoder = new window.google.maps.Geocoder();

        geocoder.geocode({ placeId }, (results, status) => {
            if (status === window.google.maps.GeocoderStatus.OK) {
                resolve(results);
            } else {
                reject(new Error(`Geocoding failed: ${status}`));
            }
        });
    });
}

function geocodeByAddress(address) {
    return new Promise((resolve, reject) => {
        if (!window.google || !window.google.maps) {
            reject(new Error('Google Maps JavaScript API library must be loaded'));
            return;
        }

        const geocoder = new window.google.maps.Geocoder();

        geocoder.geocode({ address }, (results, status) => {
            if (status === window.google.maps.GeocoderStatus.OK) {
                resolve(results);
            } else {
                reject(new Error(`Geocoding failed: ${status}`));
            }
        });
    });
}

function getLatLng(results) {
    return new Promise((resolve, reject) => {
        if (!results || !results.length) {
            reject(new Error('No results provided'));
            return;
        }

        const result = results[0];
        if (!result.geometry || !result.geometry.location) {
            reject(new Error('No geometry location found in result'));
            return;
        }

        const location = result.geometry.location;
        resolve({
            lat: location.lat(),
            lng: location.lng()
        });
    });
}

function PlacesAutocomplete({
    value,
    onChange,
    onSelect,
    onError,
    children,
    searchOptions,
    debounce: debounceMs = 200,
    highlightFirstSuggestion = false,
    shouldFetchSuggestions = true
}) {
    const [suggestions, setSuggestions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [ready, setReady] = useState(false);

    const mousedownOnSuggestion = useRef(false);
    const sessionTokenRef = useRef(null);

    useEffect(() => {
        (async () => {
            try {
                await window.google.maps.importLibrary('places');
                sessionTokenRef.current = new window.google.maps.places.AutocompleteSessionToken();
                setReady(true);
            } catch (e) {
                console.error('Failed to load Places API', e);
                if (onError) onError(e);
            }
        })();
    }, [onError]);

    const clearSuggestions = useCallback(() => setSuggestions([]), []);

    const fetchSuggestions = useCallback(
        async (input = '') => {
            if (!input) {
                setSuggestions([]);
                return;
            }

            try {
                const { AutocompleteSuggestion } = await window.google.maps.importLibrary('places');
                setLoading(true);

                const response = await AutocompleteSuggestion.fetchAutocompleteSuggestions({
                    input,
                    sessionToken: sessionTokenRef.current,
                    ...searchOptions
                });

                setSuggestions(
                    response.suggestions.map((suggestion, idx) => ({
                        id: (suggestion.placePrediction && suggestion.placePrediction.placeId) || suggestion.id,
                        description:
                            (suggestion.placePrediction &&
                                suggestion.placePrediction.text &&
                                suggestion.placePrediction.text.text) ||
                            '',
                        placeId: suggestion.placePrediction && suggestion.placePrediction.placeId,
                        types: (suggestion.placePrediction && suggestion.placePrediction.types) || [],
                        terms: (suggestion.placePrediction && suggestion.placePrediction.terms) || [],
                        formattedSuggestion: formattedSuggestion(suggestion.placePrediction),
                        index: idx,
                        active: highlightFirstSuggestion && idx === 0
                    }))
                );
            } catch (err) {
                console.error('Places fetch error', err);
                if (onError) onError(err.status || err, clearSuggestions);
            } finally {
                setLoading(false);
            }
        },
        [highlightFirstSuggestion, onError, searchOptions, clearSuggestions]
    );

    const debouncedFetch = useCallback(debounce(fetchSuggestions, debounceMs), [fetchSuggestions, debounceMs]);

    const handleInputChange = e => {
        const newValue = e.target.value;
        onChange(newValue);
        if (newValue && shouldFetchSuggestions) debouncedFetch(newValue);
        else clearSuggestions();
    };

    const handleSelect = (description, placeId, suggestion) => {
        clearSuggestions();
        if (onSelect) onSelect(description, placeId, suggestion);
        else onChange(description);
    };

    const setActiveAtIndex = index => {
        setSuggestions(prev => prev.map((s, i) => ({ ...s, active: i === index })));
    };

    const getActiveSuggestion = () => suggestions.find(s => s.active);

    const handleKeyDown = e => {
        const active = getActiveSuggestion();
        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                if (!active) setActiveAtIndex(0);
                else setActiveAtIndex((active.index + 1) % suggestions.length);
                break;
            case 'ArrowUp':
                e.preventDefault();
                if (!active) setActiveAtIndex(suggestions.length - 1);
                else setActiveAtIndex((active.index - 1 + suggestions.length) % suggestions.length);
                break;
            case 'Enter':
                e.preventDefault();
                if (active) handleSelect(active.description, active.placeId, active);
                else handleSelect(value, null, null);
                break;
            case 'Escape':
                clearSuggestions();
                break;
            default:
                break;
        }
    };

    const getInputProps = (options = {}) => ({
        ...options,
        type: 'text',
        value,
        onChange: handleInputChange,
        onKeyDown: compose(handleKeyDown, options.onKeyDown),
        autoComplete: 'off',
        disabled: !ready
    });

    const getSuggestionItemProps = (suggestion, options = {}) => ({
        ...options,
        key: suggestion.id,
        role: 'option',
        onMouseEnter: compose(() => setActiveAtIndex(suggestion.index), options.onMouseEnter),
        onMouseDown: compose(() => (mousedownOnSuggestion.current = true), options.onMouseDown),
        onMouseUp: compose(() => (mousedownOnSuggestion.current = false), options.onMouseUp),
        onClick: compose(e => {
            e.preventDefault();
            handleSelect(suggestion.description, suggestion.placeId, suggestion);
        }, options.onClick)
    });

    return typeof children === 'function'
        ? children({
              getInputProps,
              getSuggestionItemProps,
              suggestions,
              loading
          })
        : null;
}

PlacesAutocomplete.propTypes = {
    value: PropTypes.string.isRequired,
    onChange: PropTypes.func.isRequired,
    onSelect: PropTypes.func,
    onError: PropTypes.func,
    children: PropTypes.func.isRequired,
    searchOptions: PropTypes.shape({
        locationRestriction: PropTypes.object, //bounds
        includedRegionCodes: PropTypes.array, //componentRestrictions
        locationBias: PropTypes.object, //location + radius
        inputOffset: PropTypes.oneOfType([PropTypes.number, PropTypes.string]), //offset
        includedPrimaryTypes: PropTypes.array //types
    }),
    debounce: PropTypes.number,
    highlightFirstSuggestion: PropTypes.bool,
    shouldFetchSuggestions: PropTypes.bool
};

export { geocodeByPlaceId, geocodeByAddress, getLatLng };
export default PlacesAutocomplete;
